import axios from 'axios';
import { useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { logger } from '@/services/logger';
import { restUrl } from '@/services/utils';
import { DataModelAttribute, PersistedItem } from '@/types/DataModel';

import { useValidationsStore } from '../../hooks/useValidationsStore';
import { useParentHierarchy } from '../queries';
import { HierarchyDataItem } from '../types';

export const useMultiReferenceData = (
    attribute: DataModelAttribute,
    fieldPath: string,
    // memberAttributes: DataModelAttribute[],
    setValueWithDirtyState: (path: string, value: any, options?: { shouldDirty?: boolean }) => void,
    isReverseMultiReferenceAttribute: boolean
) => {
    const [loadingInitialItems, setLoadingInitialItems] = useState(true);

    // Cache for extended data to persist across state changes
    const extendedDataCache = useRef<Map<string, PersistedItem>>(new Map());
    // Keep track of the last successfully loaded reverse hierarchy data
    const lastLoadedReverseHierarchyData = useRef<PersistedItem[]>([]);
    const lastProcessedPrimaryKey = useRef<string | null>(null);

    const { addLocalValidationError } = useValidationsStore(useShallow(state => ({
        addLocalValidationError: state.addLocalValidationError,
    })));

    // Watch the current item's primary key for reverse hierarchy attributes
    const currentItemPrimaryKey = useWatch({
        name: 'primaryKey__'
    });

    // Get hierarchy name for reverse hierarchy attributes
    const hierarchyName = attribute.params?.hierarchyName;

    // Use the parent hierarchy query for reverse hierarchy attributes
    const { data: hierarchyData, isLoading: hierarchyLoading, error: hierarchyError } = useParentHierarchy({
        variables: {
            primaryKey__: currentItemPrimaryKey,
            hierarchyName: hierarchyName || ''
        },
        enabled: isReverseMultiReferenceAttribute && !!currentItemPrimaryKey && !!hierarchyName
    });

    // Extend the referenced items so we can show all the attribute values of those items
    const extendReferencedItems = async (currentValue: PersistedItem[]) => {
        if (isReverseMultiReferenceAttribute) {
            // Handle reverse hierarchy logic
            // For reverse hierarchy attributes, we need to:
            // 1. Get the current item's primary key
            // 2. Fetch hierarchy data using the parentHierarchy endpoint
            // 3. Find hierarchy items where the current item is a child
            // 4. Extract the parent primary keys from those hierarchy items
            // 5. Fetch the actual parent items using findByPrimaryKey
            // 6. Merge the parent items with relation values from the current child
            // 7. Display the parent items with relation values in the grid

            // If we don't have a primary key, don't clear existing data
            // This prevents clearing data during save operations
            if (!currentItemPrimaryKey) {
                // If we have previously loaded data, keep it
                if (lastLoadedReverseHierarchyData.current.length > 0) {
                    setValueWithDirtyState(fieldPath, lastLoadedReverseHierarchyData.current);
                } else {
                    // Even if no hierarchy data, preserve any newly added items
                    const newlyAddedItems = Array.from(extendedDataCache.current.values())
                        .filter(item => item.action__ === 'add');
                    if (newlyAddedItems.length > 0) {
                        setValueWithDirtyState(fieldPath, newlyAddedItems);
                    }
                }
                setLoadingInitialItems(false);
                return;
            }

            if (!hierarchyName) {
                setLoadingInitialItems(false);
                return;
            }

            // Check if we've already processed this primary key to avoid unnecessary re-fetches
            if (lastProcessedPrimaryKey.current === currentItemPrimaryKey) {
                // If we have data for this primary key, ensure it's set
                if (lastLoadedReverseHierarchyData.current.length > 0 && !currentValue) {
                    setValueWithDirtyState(fieldPath, lastLoadedReverseHierarchyData.current);
                } else if (!currentValue) {
                    // Even if no hierarchy data, preserve any newly added items
                    const newlyAddedItems = Array.from(extendedDataCache.current.values())
                        .filter(item => item.action__ === 'add');
                    if (newlyAddedItems.length > 0) {
                        setValueWithDirtyState(fieldPath, newlyAddedItems);
                    }
                }
                setLoadingInitialItems(false);
                return;
            }

            try {
                // Use the hierarchy data from the query
                if (hierarchyLoading) {
                    setLoadingInitialItems(true);
                    return;
                }

                if (hierarchyError) {
                    logger.error('Error loading reverse hierarchy data:', hierarchyError);
                    // On error, keep the last successfully loaded data if available
                    // Also preserve any newly added items
                    const newlyAddedItems = Array.from(extendedDataCache.current.values())
                        .filter(item => item.action__ === 'add');

                    if (lastLoadedReverseHierarchyData.current.length > 0) {
                        const itemsToRestore = [...lastLoadedReverseHierarchyData.current, ...newlyAddedItems];
                        setValueWithDirtyState(fieldPath, itemsToRestore);
                    } else {
                        setValueWithDirtyState(fieldPath, newlyAddedItems);
                    }
                    setLoadingInitialItems(false);
                    return;
                }

                if (hierarchyData && hierarchyData.length > 0) {
                    // For reverse hierarchy, we want to show the parent items that contain the current item as a child
                    // This means if item B is a child of item A, we show item A in the MultiReference
                    // and merge it with the relation values from the current child
                    const parentPrimaryKeys: string[] = [];
                    const parentRelationValuesMap = new Map<string, Record<string, any>>();

                    hierarchyData.forEach((hierarchyItem: HierarchyDataItem) => {
                        // Check if the current item is a child in this hierarchy
                        if (hierarchyItem.childPrimaryKeys && Array.isArray(hierarchyItem.childPrimaryKeys)) {
                            const isCurrentItemChild = hierarchyItem.childPrimaryKeys.includes(currentItemPrimaryKey);

                            if (isCurrentItemChild) {
                                // The current item is a child, so we want to show the parent
                                const parentKey = hierarchyItem.primaryKey;
                                if (typeof parentKey === 'string' && parentKey.trim()) {
                                    parentPrimaryKeys.push(parentKey);

                                    // Get the relation values for the current child
                                    if (hierarchyItem.childRelationValues && hierarchyItem.childRelationValues[currentItemPrimaryKey]) {
                                        const relationValues = hierarchyItem.childRelationValues[currentItemPrimaryKey];
                                        if (relationValues && typeof relationValues === 'object') {
                                            parentRelationValuesMap.set(parentKey, relationValues);
                                        }
                                    }
                                }
                            }
                        }
                    });

                    // Remove duplicates
                    const uniqueParentPrimaryKeys = [...new Set(parentPrimaryKeys)];

                    if (uniqueParentPrimaryKeys.length > 0) {
                        // Fetch the actual parent items using findByPrimaryKey
                        const response = await axios.post<PersistedItem[]>(restUrl('findByPrimaryKey'), {
                            items: uniqueParentPrimaryKeys,
                            fields: [],
                            includeValidations: true,
                        });

                        const loadedParentItems = response.data;

                        // Merge the parent items with the relation values from the current child
                        const itemsWithRelationValues = loadedParentItems.map((parentItem: PersistedItem) => {
                            const relationValues = parentRelationValuesMap.get(parentItem.primaryKey__) || {};

                            // Cache the extended item (with relation values)
                            const extendedItem = {
                                ...parentItem,
                                ...relationValues
                            };
                            extendedDataCache.current.set(parentItem.primaryKey__, extendedItem);

                            return extendedItem;
                        });

                        // Set the parent items with relation values as the current value
                        // But first, preserve any newly added items that were added by the user
                        const newlyAddedItems = Array.from(extendedDataCache.current.values())
                            .filter(item => item.action__ === 'add');

                        const finalItems = [...itemsWithRelationValues, ...newlyAddedItems];
                        setValueWithDirtyState(fieldPath, finalItems);

                        // Track the successfully loaded data (including newly added items)
                        // But filter out any items that were marked for removal
                        lastLoadedReverseHierarchyData.current = finalItems.filter(item => item.action__ !== 'remove');
                        lastProcessedPrimaryKey.current = currentItemPrimaryKey;
                    } else {
                        // No hierarchy items found, but preserve any newly added items
                        const newlyAddedItems = Array.from(extendedDataCache.current.values())
                            .filter(item => item.action__ === 'add');

                        setValueWithDirtyState(fieldPath, newlyAddedItems);
                        lastLoadedReverseHierarchyData.current = newlyAddedItems.filter(item => item.action__ !== 'remove');
                        lastProcessedPrimaryKey.current = currentItemPrimaryKey;
                    }
                } else {
                    // No hierarchy data, but preserve any newly added items
                    const newlyAddedItems = Array.from(extendedDataCache.current.values())
                        .filter(item => item.action__ === 'add');

                    setValueWithDirtyState(fieldPath, newlyAddedItems);
                    lastLoadedReverseHierarchyData.current = newlyAddedItems.filter(item => item.action__ !== 'remove');
                    lastProcessedPrimaryKey.current = currentItemPrimaryKey;
                }
            } catch (error) {
                logger.error('Error loading reverse hierarchy data:', error);
                // On error, keep the last successfully loaded data if available
                // Also preserve any newly added items
                const newlyAddedItems = Array.from(extendedDataCache.current.values())
                    .filter(item => item.action__ === 'add');

                if (lastLoadedReverseHierarchyData.current.length > 0) {
                    // Filter out any items that were marked for removal
                    const nonRemovedItems = lastLoadedReverseHierarchyData.current.filter(item => item.action__ !== 'remove');
                    const itemsToRestore = [...nonRemovedItems, ...newlyAddedItems];
                    setValueWithDirtyState(fieldPath, itemsToRestore);
                } else {
                    setValueWithDirtyState(fieldPath, newlyAddedItems);
                }
            }
        } else if (!isReverseMultiReferenceAttribute && currentValue && currentValue.length > 0) {
            // Original logic for regular multi-reference
            // Check which items need to be fetched (not in cache)
            const itemsToFetch = currentValue.filter((referencedItem: PersistedItem) =>
                !extendedDataCache.current.has(referencedItem.primaryKey__)
            );

            if (itemsToFetch.length > 0) {
                const response = await axios.post<PersistedItem[]>(restUrl('findByPrimaryKey'), {
                    items: itemsToFetch.map((referencedItem: PersistedItem) => referencedItem.primaryKey__),
                    fields: [],
                    includeValidations: true,
                });

                const loadedItems = response.data;
                // Cache the loaded items
                loadedItems.forEach(item => {
                    extendedDataCache.current.set(item.primaryKey__, item);
                });
            }

            // Create extended items using cached data
            const extendedItems = currentValue?.map((referencedItem: PersistedItem, index: number) => {
                const cachedItem = extendedDataCache.current.get(referencedItem.primaryKey__);

                // If we didn't find a referenced item in the cache, then it is deleted.
                if (!cachedItem) {
                    // Mark the whole row as invalid
                    addLocalValidationError(`${fieldPath}.${index}`, {
                        attributeName: attribute.name,
                        level: 'Failure',
                        name: 'MULTI_REFERENCE.REFERENCED_ITEM_NOT_FOUND',
                        message: 'MULTI_REFERENCE.REFERENCED_ITEM_NOT_FOUND',
                        path: [],
                    });
                }

                return {
                    ...referencedItem,
                    ...cachedItem
                };
            });
            setValueWithDirtyState(fieldPath, extendedItems);
        }

        setLoadingInitialItems(false);
    };

    // Enhanced effect to handle data restoration and preservation of newly added items
    const handleDataRestoration = (currentValue: any) => {
        if (isReverseMultiReferenceAttribute) {
            if (currentValue === undefined) {
                // If the current value is undefined (cleared during save), we need to restore data
                // We should preserve both the loaded hierarchy data and any newly added items
                // But filter out any items that were marked for removal
                const itemsToRestore = lastLoadedReverseHierarchyData.current.filter(item => item.action__ !== 'remove');

                // Check if there are any items in the extended data cache that were added by the user
                // These would have action__: 'add' and should be preserved
                extendedDataCache.current.forEach((cachedItem, primaryKey) => {
                    if (cachedItem.action__ === 'add') {
                        // Check if this item is not already in the items to restore
                        const alreadyExists = itemsToRestore.some(item => item.primaryKey__ === primaryKey);
                        if (!alreadyExists) {
                            itemsToRestore.push(cachedItem);
                        }
                    }
                });

                if (itemsToRestore.length > 0) {
                    setValueWithDirtyState(fieldPath, itemsToRestore, { shouldDirty: false });
                }
            } else if (!currentValue && lastLoadedReverseHierarchyData.current.length > 0) {
                // If the current value is empty but we have previously loaded data, restore it
                // This handles the case where data is cleared during save operations
                // But filter out any items that were marked for removal
                const itemsToRestore = lastLoadedReverseHierarchyData.current.filter(item => item.action__ !== 'remove');
                setValueWithDirtyState(fieldPath, itemsToRestore, { shouldDirty: false });
            }
        }
    };

    // Cache newly added item for reverse hierarchy attributes
    const cacheNewlyAddedItem = (item: any) => {
        if (isReverseMultiReferenceAttribute && item.primaryKey__) {
            extendedDataCache.current.set(item.primaryKey__, { ...item });
        }
    };

    return {
        loadingInitialItems,
        hierarchyLoading,
        hierarchyError,
        hierarchyData,
        currentItemPrimaryKey,
        extendedDataCache,
        extendReferencedItems,
        handleDataRestoration,
        cacheNewlyAddedItem
    };
};
